import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApolloError } from 'apollo-boost';

import {
	CheckinOrderFragment,
	OrderSelectedServices,
	TravellerFieldEnum,
	useCheckinSaveOrderServicesMutation,
	useGetCheckinOrderQuery,
	useSetCheckinSetTravellersDataMutation,
	useUpdateCheckinOrderMutation
} from '@websky/graphql';
import {
	CartService,
	CartServiceAeroexpress,
	CartServiceGds,
	CartServiceInsurance,
	OnDeleteItemType
} from '../../Cart/types';
import { RunServiceRequestPassengers } from '../../Checkout/types';
import { getCheckinOrder, getCheckinOrderId, getHasRequestedOrderServices } from './store/order/selectors';
import { addService, removeService, saveServices } from './store/selectedServices/actions';
import { fillCheckinOrder, setCheckinOrder } from './store/order/actions';
import { useServiceRequest } from './utils';
import { CheckinStep, SaveLoyaltyNumberData } from './types';
import { useConditionalPolling } from '../../hooks';
import { ServicePayload } from '../../Checkout/store/selectedServices/actions';
import { useConfig } from '../../context';
import { useStatic } from '@shared/lib/static';
import { RestrictionsResponse } from '@shared/ui/Restrictions';
import { setRestrictions, setRestrictionsLoading } from '@entities/restrictions';
import { Log } from '../../sentry';
import { StaticException } from '@shared/error';

export const useCheckinOrderServices = (
	onRefetchOrder: () => Promise<CheckinOrderFragment>,
	onRequestError: (error: ApolloError) => void,
	setStep: (step: CheckinStep) => void
) => {
	const dispatch = useDispatch();

	const order = useSelector(getCheckinOrder);

	const [saveOrderServices] = useCheckinSaveOrderServicesMutation();
	const { runServiceRequest } = useServiceRequest();

	const [isLoading, setIsLoading] = useState(false);

	const onRequest = useCallback(async (passengers: RunServiceRequestPassengers[]) => {
		try {
			await runServiceRequest(passengers);

			return onRefetchOrder();
		} catch (e) {
			const apolloError = e as ApolloError;

			if (apolloError?.graphQLErrors?.length) {
				onRequestError(apolloError);
			}

			return null;
		}
	}, []);

	const onSaveServices = useCallback(
		async (params: OrderSelectedServices) => {
			try {
				const { data } = await saveOrderServices({ variables: { params } });

				if (data.CheckinSaveOrderServices) {
					dispatch(setCheckinOrder(data.CheckinSaveOrderServices));
				}

				return data.CheckinSaveOrderServices;
			} catch (e) {
				const apolloError = e as ApolloError;

				if (apolloError?.graphQLErrors?.length) {
					onRequestError(apolloError);
				}

				return null;
			}
		},
		[dispatch]
	);

	const onSaveInsurance = useCallback(
		async (insuranceCode: string) => {
			const insurances = order.additionalServices.insurances;

			const selectedInsurances = insurances?.selectedInsurances
				.filter(selected => selected.insuranceProgram.code !== insuranceCode)
				.map(selected => selected.insuranceProgram);

			const selectedTravellers = insurances?.travellerExtraValues || [];
			const selectedPrograms = selectedInsurances.map(selected => selected.code);

			await onSaveServices({
				id: order.id,
				insurances: [
					{
						selectedPrograms,
						selectedProgramsByTravellers: selectedTravellers.map(traveller => ({
							travellerId: traveller.traveller.id,
							programCodes: selectedPrograms,
							travellerDocumentNumber: ''
						}))
					}
				]
			});
		},
		[order]
	);

	const onSaveAeroexpress = useCallback(
		async (tripId: string) => {
			const selectedTrips = order.additionalServices.transfer?.aeroexpress?.selectedTrips || [];

			await onSaveServices({
				id: order.id,
				aeroexpress: {
					selectedTrips: selectedTrips.filter(trip => trip.id !== tripId).map(trip => trip.id)
				}
			});
		},
		[order]
	);

	const onDeleteItem: OnDeleteItemType = useCallback(
		async (type, service) => {
			setIsLoading(true);

			const services = order.additionalServices?.gdsServices?.services || [];

			if ([CartService.Baggage, CartService.Meal, CartService.BusinessLounge].includes(type)) {
				const cartService = service as CartServiceGds;
				const gdsService = services.find(gdsService => gdsService.id === cartService.serviceId);

				const segmentIds = cartService.allSegments
					? order.segments.map(segment => segment.id)
					: [cartService.segmentId];

				segmentIds.forEach(segmentId => {
					dispatch(
						removeService({
							service: gdsService,
							passengerId: cartService.passengerId,
							segmentId
						})
					);
				});

				dispatch(saveServices());
			} else if (type === CartService.Seats) {
				const cartService = service as CartServiceGds;

				const saveTravellers: RunServiceRequestPassengers[] = order.travellers.map(traveller => ({
					passengerId: traveller.id,
					seats: cartService.passengerId === traveller.id ? null : traveller.services?.seats,
					setServices: null
				}));

				setStep(CheckinStep.Seats);

				await onRequest(saveTravellers);
			} else if (type === CartService.Insurances) {
				const cartServiceInsurance = service as CartServiceInsurance;

				await onSaveInsurance(cartServiceInsurance.code);
			} else if (type === CartService.Aeroexpress) {
				const cartServiceAeroexpress = service as CartServiceAeroexpress;

				await onSaveAeroexpress(cartServiceAeroexpress.tripId);
			}

			setIsLoading(false);
		},
		[order.additionalServices, dispatch]
	);

	return { isLoading, onDeleteItem };
};

export const useUpdateCheckinTravellerServices = () => {
	const dispatch = useDispatch();

	const order = useSelector(getCheckinOrder);
	const hasRequestedOrderServices = useSelector(getHasRequestedOrderServices);

	const [updateCheckinOrder] = useUpdateCheckinOrderMutation();
	const { refetch: fetchCheckinOrder } = useGetCheckinOrderQuery({ fetchPolicy: 'no-cache', skip: true });

	const onUpdateOrder = useCallback(async () => {
		const updateOrderResponse = await updateCheckinOrder({ variables: { id: order.id } });

		if (updateOrderResponse?.data?.UpdateCheckinOrder) {
			const checkinOrderResponse = await fetchCheckinOrder({ id: order.id });

			if (checkinOrderResponse?.data?.CheckinOrder) {
				dispatch(fillCheckinOrder(checkinOrderResponse.data.CheckinOrder));
			}
		}
	}, [order?.id, updateCheckinOrder, fetchCheckinOrder]);

	useConditionalPolling(onUpdateOrder, hasRequestedOrderServices, {
		interval: 5000,
		progressiveIntervalPerQuery: true
	});
};

export function useOrderServices() {
	const dispatch = useDispatch();

	const order = useSelector(getCheckinOrder);

	const [saveOrderServices, saveOrderServicesResult] = useCheckinSaveOrderServicesMutation();
	const [requestError, setRequestError] = useState<string>();

	// Effects
	useEffect(() => {
		if (saveOrderServicesResult?.data?.CheckinSaveOrderServices) {
			dispatch(setCheckinOrder(saveOrderServicesResult.data.CheckinSaveOrderServices));
		}
	}, [saveOrderServicesResult?.data]);

	// Methods
	const onRequestError = useCallback(
		(e: unknown) => {
			if (e instanceof ApolloError) {
				if (e.graphQLErrors?.length) {
					setRequestError(e.graphQLErrors[0].message);
				}
			} else if (e instanceof Error) {
				setRequestError(e.message);
			} else {
				setRequestError('Unknown error');
			}
		},
		[setRequestError]
	);

	const onClearError = useCallback(() => setRequestError(undefined), [setRequestError]);

	const onSaveServicesRequest = useCallback(
		async (params: OrderSelectedServices) => {
			try {
				const { data } = await saveOrderServices({ variables: { params } });

				return data?.CheckinSaveOrderServices;
			} catch (e) {
				onRequestError(e);

				return null;
			}
		},
		[onRequestError]
	);

	const onAddServices = useCallback(
		(services: ServicePayload[]) => {
			services.forEach(service => {
				const relatedGdsService = order.additionalServices?.gdsServices?.services?.find(
					gdsService => gdsService.id === service.serviceId
				);

				dispatch(
					addService({
						service: relatedGdsService,
						segmentId: service.segmentId,
						passengerId: service.passengerId
					})
				);
			});

			dispatch(saveServices());
		},
		[dispatch, order.additionalServices]
	);

	const onRemoveServices = useCallback(
		(services: ServicePayload[]) => {
			services.forEach(service => {
				const relatedGdsService = order.additionalServices?.gdsServices?.services?.find(
					gdsService => gdsService.id === service.serviceId
				);

				dispatch(
					removeService({
						service: relatedGdsService,
						segmentId: service.segmentId,
						passengerId: service.passengerId
					})
				);
			});

			dispatch(saveServices());
		},
		[dispatch, order.additionalServices]
	);

	const onSaveServices = useCallback(
		(services: ServicePayload[]) => {
			const servicesToAdd: ServicePayload[] = [];
			const servicesToRemove: ServicePayload[] = [];

			services.forEach(service => {
				if (service.count > 0) {
					servicesToAdd.push(service);
				} else {
					servicesToRemove.push(service);
				}
			});

			if (servicesToAdd.length) {
				onAddServices(servicesToAdd);
			}

			if (servicesToRemove.length) {
				onRemoveServices(servicesToRemove);
			}
		},
		[dispatch, onAddServices, onRemoveServices]
	);

	return {
		// data
		requestError,
		saveOrderServicesResult,
		isLoading: saveOrderServicesResult.loading,

		// methods
		saveOrderServices,
		onRequestError,
		onClearError,
		onSaveServicesRequest,
		onAddServices,
		onRemoveServices,
		onSaveServices
	};
}

export const useSaveLoyaltyNumber = ({ onError }: { onError: (error: Error) => void }) => {
	const dispatch = useDispatch();

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const orderId = useSelector(getCheckinOrderId);

	const [setTravellerData, { loading, error }] = useSetCheckinSetTravellersDataMutation();

	const onSaveLoyaltyNumber = async ({ travellerId, code }: SaveLoyaltyNumberData) => {
		const { data, errors } = await setTravellerData({
			variables: {
				params: {
					orderId,
					travellersData: [
						{
							id: travellerId,
							values: [
								{
									type: TravellerFieldEnum.LoyaltyNumber,
									name: 'loyaltyNumber',
									value: code
								},
								{
									type: TravellerFieldEnum.LoyaltyAirline,
									name: 'loyaltyAirline',
									value: iataCode
								}
							]
						}
					]
				}
			}
		});

		const checkinSetTravellersData = data?.CheckinSetTravellersData;

		if (errors?.length) {
			onError(errors[0]);

			return;
		}

		if (!checkinSetTravellersData) {
			onError(new Error('Unknown error'));

			return;
		}

		dispatch(fillCheckinOrder(checkinSetTravellersData));
	};

	useEffect(() => {
		if (error) {
			onError(error);
		}
	}, [error]);

	return {
		onSaveLoyaltyNumber,
		loading
	};
};

export const useCheckinRestrictions = () => {
	const dispatch = useDispatch();
	const {
		Checkin: { checkinRestrictionsEnabled = false }
	} = useConfig();
	const { data, loading, error } = useStatic<RestrictionsResponse>({
		path: 'configs/CheckinRestrictions.json',
		responseType: 'json',
		skip: !checkinRestrictionsEnabled
	});

	useEffect(() => {
		if (!data && checkinRestrictionsEnabled) {
			const error = new StaticException('The restrictions step is enabled, but the json has not been added.');

			Log({
				category: error.name,
				level: 'error',
				message: error.message
			});
		}

		if (data) {
			dispatch(setRestrictions(data.restrictions));
		}
	}, [data, checkinRestrictionsEnabled, dispatch]);

	useEffect(() => {
		dispatch(setRestrictionsLoading(loading));
	}, [loading, dispatch]);

	return { data, loading, error };
};
